'use client';

/**
 * DataGridFilterControls — Phase F1 Sprint 1.
 *
 * Renders filter SETTER widgets in the toolbar. The Phase A1 toolbar only
 * displayed active filter CHIPS (read-only + clear); consumers had to
 * mount their own `<select>` widgets above the grid to actually SET
 * filters. That ergonomic gap surfaced repeatedly through Phases B-D
 * (e.g. Sprint 5 tasks, Sprint 15 help-tickets) and was made explicit
 * by the F1 audit.
 *
 * This component closes the loop: given a `FilterSpec[]` + an
 * `activeFilters` map + an `onFilterChange` callback, render the right
 * widget per `FilterSpec.kind`:
 *   - `text`     — debounced text input
 *   - `select`   — native `<select>` over `spec.options`
 *   - `multi-select` — popover with checkbox list
 *   - `date-range`  — two date inputs
 *   - `boolean`     — tri-state segmented control (any / yes / no)
 *
 * Consumers still control the underlying query — this is a pure setter
 * surface that emits values via `onFilterChange`. Active values render
 * as chips via the existing `data-grid-toolbar.tsx`.
 */

import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { FilterSpec, ActiveFilters } from './types';
import { labelForOption } from './filter-options';
import { DateEntryInput } from '../date-entry-input';

export interface DataGridFilterControlsProps {
  filters: ReadonlyArray<FilterSpec>;
  activeFilters: ActiveFilters;
  /**
   * Fired when any individual filter dimension changes. Value shape
   * depends on `FilterSpec.kind` (string / string[] / { from, to } / boolean).
   */
  onFilterChange: (filterId: string, value: unknown) => void;
}

export function DataGridFilterControls({
  filters,
  activeFilters,
  onFilterChange,
}: DataGridFilterControlsProps) {
  if (filters.length === 0) return null;
  return (
    <div
      className="experience-candidate-data-grid flex w-full min-w-0 flex-wrap items-center gap-1.5"
      data-data-grid-filter-controls
    >
      {filters.map((spec) => (
        <FilterWidget
          key={spec.id}
          spec={spec}
          value={activeFilters[spec.id]}
          onChange={(v) => onFilterChange(spec.id, v)}
        />
      ))}
    </div>
  );
}

interface FilterWidgetProps {
  spec: FilterSpec;
  value: unknown;
  onChange: (v: unknown) => void;
}

function FilterWidget({ spec, value, onChange }: FilterWidgetProps) {
  switch (spec.kind) {
    case 'text':
      return <TextFilter spec={spec} value={value as string | undefined} onChange={onChange} />;
    case 'select':
      return <SelectFilter spec={spec} value={value as string | undefined} onChange={onChange} />;
    case 'multi-select':
      return (
        <MultiSelectFilter spec={spec} value={value as string[] | undefined} onChange={onChange} />
      );
    case 'date-range':
      return (
        <DateRangeFilter
          spec={spec}
          value={value as { from?: string; to?: string } | undefined}
          onChange={onChange}
        />
      );
    case 'boolean':
      return <BooleanFilter spec={spec} value={value as boolean | undefined} onChange={onChange} />;
    default:
      return null;
  }
}

// ─── Text ────────────────────────────────────────────────────────────────

function TextFilter({
  spec,
  value,
  onChange,
}: {
  spec: FilterSpec;
  value: string | undefined;
  onChange: (v: unknown) => void;
}) {
  const [local, setLocal] = useState(value ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setLocal(value ?? '');
  }, [value]);
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );
  return (
    <input
      type="text"
      value={local}
      placeholder={spec.label}
      aria-label={spec.label}
      onChange={(e) => {
        setLocal(e.target.value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          onChange(e.target.value || undefined);
        }, 250);
      }}
      className={cn(
        'h-8 w-full min-w-[10rem] rounded-md border border-slate-200 bg-white px-2.5 text-[11.5px] text-statura-ink placeholder:text-text-muted sm:w-auto',
        'focus:border-statura-green-accessible focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible/40',
      )}
    />
  );
}

// ─── Select ──────────────────────────────────────────────────────────────

function SelectFilter({
  spec,
  value,
  onChange,
}: {
  spec: FilterSpec;
  value: string | undefined;
  onChange: (v: unknown) => void;
}) {
  return (
    <select
      value={value ?? ''}
      aria-label={spec.label}
      onChange={(e) => onChange(e.target.value || undefined)}
      className={cn(
        'h-8 w-full min-w-[10rem] max-w-full rounded-md border border-slate-200 bg-white px-2 pr-7 text-[11.5px] text-statura-ink sm:w-auto',
        'focus:border-statura-green-accessible focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible/40',
      )}
    >
      <option value="">All {spec.label.toLowerCase()}</option>
      {(spec.options ?? []).map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

// ─── Multi-select (popover with checkboxes) ─────────────────────────────

function MultiSelectFilter({
  spec,
  value,
  onChange,
}: {
  spec: FilterSpec;
  value: string[] | undefined;
  onChange: (v: unknown) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const current = value ?? [];
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);
  const label =
    current.length === 0
      ? spec.label
      : current.length === 1
        ? `${spec.label}: ${labelForOption(spec, current[0]!)}`
        : `${spec.label}: ${current.length} selected`;
  return (
    <div ref={rootRef} className="relative w-full min-w-0 sm:w-auto">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${spec.label} filter`}
        className={cn(
          'inline-flex h-8 w-full max-w-full min-w-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 text-left text-[11.5px] font-medium text-statura-ink whitespace-normal break-words sm:w-auto [overflow-wrap:anywhere]',
          'hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible',
        )}
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <ChevronDown className="h-3 w-3 shrink-0" aria-hidden="true" />
      </button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          aria-label={spec.label}
          className="absolute left-0 z-30 mt-1 max-h-72 w-full min-w-56 overflow-auto rounded-md border border-slate-200 bg-white p-1 shadow-lg sm:w-56"
        >
          {(spec.options ?? []).map((opt) => {
            const checked = current.includes(opt.value);
            return (
              <label
                key={opt.value}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-[11.5px]',
                  'hover:bg-slate-50 focus-within:bg-slate-50',
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const next = checked
                      ? current.filter((v) => v !== opt.value)
                      : [...current, opt.value];
                    onChange(next.length === 0 ? undefined : next);
                  }}
                  className="h-3.5 w-3.5"
                  aria-label={opt.label}
                />
                <span className="min-w-0 break-words text-statura-ink [overflow-wrap:anywhere]">
                  {opt.label}
                </span>
              </label>
            );
          })}
          {current.length > 0 && (
            <button
              type="button"
              onClick={() => onChange(undefined)}
              className="mt-1 w-full rounded px-2 py-1 text-left text-[10.5px] font-medium text-text-muted hover:bg-slate-50 hover:text-statura-ink"
            >
              Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Date range ──────────────────────────────────────────────────────────

function DateRangeFilter({
  spec,
  value,
  onChange,
}: {
  spec: FilterSpec;
  value: { from?: string; to?: string } | undefined;
  onChange: (v: unknown) => void;
}) {
  const current = value ?? {};
  const merge = (patch: { from?: string; to?: string }) => {
    const next = { ...current, ...patch };
    if (!next.from && !next.to) {
      onChange(undefined);
    } else {
      onChange(next);
    }
  };
  return (
    <div
      className="flex w-full min-w-0 flex-wrap items-center gap-1 sm:w-auto"
      role="group"
      aria-label={spec.label}
    >
      <DateEntryInput
        value={current.from ?? ''}
        onChange={(nextValue) => merge({ from: nextValue || undefined })}
        aria-label={`${spec.label} from`}
        className="h-8 min-w-[8.5rem] rounded-md border border-slate-200 bg-white px-2 text-[11.5px] text-statura-ink"
      />
      <span className="text-[10.5px] text-text-muted">→</span>
      <DateEntryInput
        value={current.to ?? ''}
        onChange={(nextValue) => merge({ to: nextValue || undefined })}
        aria-label={`${spec.label} to`}
        className="h-8 min-w-[8.5rem] rounded-md border border-slate-200 bg-white px-2 text-[11.5px] text-statura-ink"
      />
    </div>
  );
}

// ─── Boolean (tri-state: any / yes / no) ────────────────────────────────

function BooleanFilter({
  spec,
  value,
  onChange,
}: {
  spec: FilterSpec;
  value: boolean | undefined;
  onChange: (v: unknown) => void;
}) {
  const states: Array<{ id: string; label: string; v: boolean | undefined }> = [
    { id: 'any', label: 'Any', v: undefined },
    { id: 'yes', label: 'Yes', v: true },
    { id: 'no', label: 'No', v: false },
  ];
  const current = value === undefined ? 'any' : value ? 'yes' : 'no';
  return (
    <div
      role="radiogroup"
      aria-label={spec.label}
      className="flex min-w-0 flex-wrap items-center rounded-md border border-slate-200 bg-slate-50 p-0.5 text-[11.5px]"
    >
      <span className="min-w-0 break-words px-1.5 text-text-muted [overflow-wrap:anywhere]">
        {spec.label}
      </span>
      {states.map((s) => (
        <button
          key={s.id}
          type="button"
          role="radio"
          aria-checked={current === s.id}
          onClick={() => onChange(s.v)}
          className={cn(
            'rounded px-2 py-0.5 font-medium transition',
            current === s.id
              ? 'bg-white text-statura-ink shadow-sm'
              : 'text-text-muted hover:text-statura-ink',
          )}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
}
